import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from 'equal-ds-ui';
import { Eye, EyeOff, X, Info } from 'lucide-react';
import type { AdminUserTableRow } from '../../../types/admin-users';
import warningIcon from '../../../assets/images/warning_icon.png';

export interface AdminUserResetPasswordDialogProps {
  user: AdminUserTableRow | null;
  isOpen: boolean;
  onConfirm: (password: string) => Promise<void> | void;
  onClose: () => void;
  isProcessing?: boolean;
  /** Optional callback to show success message in parent (for backwards compatibility) */
  onSuccess?: (message: string) => void;
}

// Password regex matching Angular implementation
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&])(?!.*\s).{8,}$/;

export const AdminUserResetPasswordDialog: React.FC<AdminUserResetPasswordDialogProps> = ({
  user,
  isOpen,
  onConfirm,
  onClose,
  isProcessing = false,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [touched, setTouched] = useState<{ password: boolean; confirmPassword: boolean }>({
    password: false,
    confirmPassword: false,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setConfirmPassword('');
      setErrors({});
      setTouched({ password: false, confirmPassword: false });
      setShowPassword(false);
      setShowConfirmPassword(false);
      setSuccessMessage(null);
      setErrorMessage(null);
    }
  }, [isOpen]);

  // Auto-close dialog after success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        onClose();
        // Optionally notify parent
        if (onSuccess) {
          onSuccess(successMessage);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, onClose, onSuccess]);

  // Validate password on blur
  const validatePassword = useCallback((value: string) => {
    if (!value) {
      return 'Password is required.';
    }
    if (!passwordRegex.test(value)) {
      return 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character.';
    }
    return undefined;
  }, []);

  // Validate confirm password
  const validateConfirmPassword = useCallback((value: string, passwordValue: string) => {
    if (!value) {
      return 'Confirm password is required.';
    }
    if (value !== passwordValue) {
      return 'Passwords do not match.';
    }
    return undefined;
  }, []);

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);

    // Re-validate confirm password if it's been touched
    if (touched.confirmPassword && confirmPassword) {
      const confirmError = validateConfirmPassword(confirmPassword, value);
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  // Handle password blur
  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    const error = validatePassword(password);
    setErrors((prev) => ({ ...prev, password: error }));
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  };

  // Handle confirm password blur
  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    const error = validateConfirmPassword(confirmPassword, password);
    setErrors((prev) => ({ ...prev, confirmPassword: error }));
  };

  // Check if form is valid (using useMemo since this is a derived value, not a callback)
  const isFormValid = useMemo(() => {
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);
    return !passwordError && !confirmError;
  }, [password, confirmPassword, validatePassword, validateConfirmPassword]);

  // Handle form submit
  const handleSubmit = async () => {
    // Validate all fields
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);

    setErrors({ password: passwordError, confirmPassword: confirmError });
    setTouched({ password: true, confirmPassword: true });
    setErrorMessage(null);

    if (passwordError || confirmError) {
      return;
    }

    try {
      await onConfirm(password);
      setSuccessMessage(`Password changed successfully for ${user?.name ?? 'user'}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reset password. Please try again.';
      setErrorMessage(message);
    }
  };

  if (!isOpen || !user) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/35 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl border border-border-default p-6 space-y-4">
        {/* Header with close button */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            Reset Password
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Warning Icon */}
        <div className="flex justify-center py-2">
          <img src={warningIcon} alt="" className="w-20 h-20 object-contain" />
        </div>

        {/* Message */}
        <p className="text-sm text-text-secondary text-center">
          Set a new password for{' '}
          <span className="font-semibold text-text-primary">{user.name}</span>
        </p>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Warning note */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Please note that this action cannot be undone.</span>
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-semibold text-text-secondary mb-2">
            New Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              placeholder="Enter new password"
              className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {touched.password && errors.password && (
            <p className="text-xs text-red-500 mt-1">{errors.password}</p>
          )}
          <p className="text-xs text-text-tertiary mt-1">
            Min 8 chars with uppercase, lowercase, number & special character (@$!%*?&)
          </p>
        </div>

        {/* Confirm Password Field */}
        <div>
          <label className="block text-sm font-semibold text-text-secondary mb-2">
            Confirm Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordBlur}
              placeholder="Confirm new password"
              className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {touched.confirmPassword && errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="equal-ds-root pt-2">
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isProcessing || !isFormValid}
            className="w-full"
          >
            {isProcessing ? 'Resetting...' : 'Reset Password'}
          </Button>
        </div>
      </div>
    </div>
  );
};
